/** Pure composers for agent-kernel `brief` and `roundContext` content. */

import { type SuperAssistantWorkflow, SuperSource } from './super'

type WorkflowStep = SuperAssistantWorkflow['steps'][number]

type ParentRef = { id: string; title?: string }

const formatParentRef = ({ id, title }: ParentRef): string =>
  title ? `${id} ("${title}")` : id

const tagged = (name: string, content: string): string =>
  `<${name}>\n${content}\n</${name}>`

const taggedIfPresent = (
  name: string,
  content: string | null | undefined | false
): string | null => (content ? tagged(name, content) : null)

/** Groups parent IDs by available reading tool or search-only use. */
const classifyParents = (
  step: WorkflowStep
): {
  getNoteRefs: ParentRef[]
  expandDocumentRefs: ParentRef[]
  searchScopes: ParentRef[]
} => {
  const detailsById = new Map(
    (step.parentDetails ?? []).map((detail) => [detail.id, detail])
  )
  const getNoteRefs: ParentRef[] = []
  const expandDocumentRefs: ParentRef[] = []
  const searchScopes: ParentRef[] = []
  for (const id of step.parentIds ?? []) {
    const detail = detailsById.get(id)
    if (!detail) {
      // Unknown sources use expandDocument if the task asks to read them.
      expandDocumentRefs.push({ id })
    } else if (detail.source === SuperSource.SLITE) {
      getNoteRefs.push({ id, title: detail.title })
    } else if (detail.source === SuperSource.GOOGLE_DRIVE) {
      expandDocumentRefs.push({ id, title: detail.title })
    } else {
      searchScopes.push({ id, title: detail.title })
    }
  }
  return { getNoteRefs, expandDocumentRefs, searchScopes }
}

/** Formats parent IDs for the tool preferred by this brief. */
type ParentsFilterSyntax = (serializedParentIds: string) => string

// The task determines whether a linked ID is context to read or a search scope.
const readableDocumentsLine = (
  refs: ParentRef[],
  readingTool: string,
  parentsFilterSyntax: ParentsFilterSyntax
): string => {
  const listedRefs = refs.map(formatParentRef).join(', ')
  const scopeSyntax = parentsFilterSyntax(
    JSON.stringify(refs.map((ref) => ref.id))
  )
  return `- **Documents/Sources**: The following document IDs are relevant: ${listedRefs}. The task decides how each is used: one it wants as context, read with \`${readingTool}\`; one it wants you to search inside (a channel, a collection, or the top of a tree of docs), pass as a scope by ${scopeSyntax} — keeping in that list only the ids you use as containers. \`${readingTool}\` returns only that one page, never the documents inside a container — do not read a container to search it.`
}

const SEARCH_PARENTS_SYNTAX: ParentsFilterSyntax = (serializedParentIds) =>
  `\`filters.parents: ${serializedParentIds}\` on the search call`

const SYNTHESIS_PARENTS_SYNTAX: ParentsFilterSyntax = (serializedParentIds) =>
  `\`parentIds: ${serializedParentIds}\` on this task's \`largeSynthesis\` topic (as \`filters.parents\` on \`progressiveSearch\`)`

// A search matches any parent ID, so keep the full step scope together.
const searchScopesLine = (
  scopes: ParentRef[],
  readableDocumentIds: string[],
  parentsFilterSyntax: ParentsFilterSyntax
): string => {
  const scopeTitles = scopes.map((s) => `"${s.title ?? s.id}"`).join(', ')
  const serializedParentIds = JSON.stringify([
    ...readableDocumentIds,
    ...scopes.map((s) => s.id),
  ])
  return `- **Source scopes**: restrict this task's searches to ${scopeTitles}${
    readableDocumentIds.length ? ' plus the documents above' : ''
  } by passing ${parentsFilterSyntax(serializedParentIds)}, verbatim. Containers are NOT documents — do not try to read them directly.`
}

const formatStep = (
  step: WorkflowStep,
  parentsFilterSyntax: ParentsFilterSyntax
): string => {
  const { getNoteRefs, expandDocumentRefs, searchScopes } =
    classifyParents(step)
  const readableDocumentIds = [...getNoteRefs, ...expandDocumentRefs].map(
    (ref) => ref.id
  )
  return [
    `### Task: ${step.explanation}`,
    step.instructions && `- **Instructions**: ${step.instructions}`,
    step.sourceTypes?.length && `- **Sources**: ${step.sourceTypes.join(', ')}`,
    step.tool &&
      `- **Suggested tool** (from an older workflow format — map to the closest available tool): \`${step.tool}\` with parameters \`${JSON.stringify(step.parameters ?? {})}\``,
    getNoteRefs.length &&
      readableDocumentsLine(getNoteRefs, 'getNote', parentsFilterSyntax),
    expandDocumentRefs.length &&
      readableDocumentsLine(
        expandDocumentRefs,
        'expandDocument',
        parentsFilterSyntax
      ),
    searchScopes.length &&
      searchScopesLine(searchScopes, readableDocumentIds, parentsFilterSyntax),
    step.type === 'map' &&
      `- **Type**: map — iterate over items from a previous task's results`,
  ]
    .filter((line): line is string => !!line)
    .join('\n')
}

const ASSISTANT_SCOPED_TOOL_RULE = `Use the scoping in the tasks above as the starting point for your tool calls: prefer \`progressiveSearch\` with the task's source types and \`parents\` filters.`

const DIGEST_SCOPED_TOOL_RULE = `Use the scoping in the tasks above as the starting point for your tool calls: resolve every container a task names with \`searchParents\` first (a task that names only source types reads them whole — do not narrow it to containers it does not name), then make ONE \`largeSynthesis\` call for the whole workflow — list every task's question under \`topics\`, each with that task's \`parentIds\` (or, for a task that names only source types, its \`sourceTypes\`), with a recomputed date window — and keep \`progressiveSearch\` for targeted sub-questions. \`largeSynthesis\` runs once per answer; a second call is refused.`

const workflowRules = (scopedToolRule: string): string =>
  [
    `Create a plan from these tasks with your first \`updatePlan\` call, then keep it current (send the complete updated step list) as you progress — mark the final steps done together WITH your final response.`,
    `${scopedToolRule} IMPORTANT: date parameters in the plan may be stale — always recompute relative date references ("last month", "past week", etc.) using the current date.`,
    `Later messages in this thread may be NEW questions — re-run the workflow steps that bear on them, with the same source scoping — or clarifications, refinements, or follow-ups about a previous answer, which you should answer directly from context — do NOT re-run the workflow or re-search unless new information is genuinely needed. The output format applies to every substantive answer.`,
    `For "map" type tasks: iterate over items from a previous task's results, executing the task for each item.`,
    `You may fill in missing parameter values using context from previous tasks' results.`,
  ]
    .map((rule) => `- ${rule}`)
    .join('\n')

const PARALLELISM_DIRECTIVE = `

**CRITICAL — PARALLEL EXECUTION REQUIRED**: You MUST call multiple tools in a single response whenever they are independent. For example, if you need to run a global search AND a source-specific tool AND a delegate consult, call ALL of them at once. Calling only one tool per turn when others are ready is a performance failure. Independent tasks = different sources, different searches, different data needs. ALWAYS maximize parallel calls (up to 5).`

/** Whether a composed brief opens with a pre-defined workflow (rather than
 *  only instructions/context) — pairs with `composeAssistantBrief`'s format. */
export const briefContainsWorkflow = (brief: string): boolean =>
  brief.startsWith('<workflow>')

/**
 * Composes an assistant workflow and author prompts for the session brief.
 * @knipignore consumed by the surfaces migrating onto `fetchAgentKernelStream`
 */
export function composeAssistantBrief(
  prompts: BriefPrompts
): string | undefined {
  return composeBriefWith(
    ASSISTANT_SCOPED_TOOL_RULE,
    SEARCH_PARENTS_SYNTAX,
    prompts
  )
}

interface BriefPrompts {
  workflow?: SuperAssistantWorkflow
  customPrompt?: string | null
  contextPrompt?: string | null
}

function composeBriefWith(
  scopedToolRule: string,
  parentsFilterSyntax: ParentsFilterSyntax,
  { workflow, customPrompt, contextPrompt }: BriefPrompts
): string | undefined {
  const instructionsSection = taggedIfPresent(
    'additional-instructions',
    customPrompt
  )
  const contextSection = taggedIfPresent('context', contextPrompt)

  if (!workflow) {
    const sections = [instructionsSection, contextSection].filter(
      (section): section is string => !!section
    )
    return sections.length ? sections.join('\n\n') : undefined
  }

  const parallelism = workflow.steps.length > 1 ? PARALLELISM_DIRECTIVE : ''

  return [
    `<workflow>
This assistant has a pre-defined workflow. It defines your sources, their scoping, and the expected output format.${parallelism}`,
    taggedIfPresent(
      'tasks',
      workflow.steps
        .map((step) => formatStep(step, parentsFilterSyntax))
        .join('\n\n')
    ),
    taggedIfPresent('output-format', workflow.outputGuidance),
    tagged('rules', workflowRules(scopedToolRule)),
    instructionsSection,
    contextSection,
    `</workflow>`,
  ]
    .filter((section): section is string => !!section)
    .join('\n\n')
}

const AUTOMATION_SYNTHESIS_INSTRUCTIONS = `
When processing this query, consider using the largeSynthesis tool to process and synthesize large volumes of documents. It is particularly useful for producing comprehensive summaries, identifying trends, or analyzing patterns across many items. It runs once per answer, so list every question you need answered as a topic of that one call. You may still use search tools to gather context or answer specific sub-questions alongside it.
`.trim()

/** The digest run's session brief: the synthesis nudge plus whatever the
 *  digest's assistant configures, with the workflow's scoped-tool rule
 *  steering to `largeSynthesis` (the assistant rule's `progressiveSearch`
 *  preference would override the nudge). Shared by the scheduled runner and
 *  the interactive digest preview. */
export function composeDigestBrief(prompts: BriefPrompts): string {
  return [
    AUTOMATION_SYNTHESIS_INSTRUCTIONS,
    composeBriefWith(
      DIGEST_SCOPED_TOOL_RULE,
      SYNTHESIS_PARENTS_SYNTAX,
      prompts
    ),
  ]
    .filter(Boolean)
    .join('\n\n')
}
